import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { CouponDefinition } from '@/lib/api-admin';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor } from '@/lib/http-core';
import { productCouponsQueryKey } from './useProductCouponsQuery';

export interface SyncProductCouponsInput {
  productId: string;
  couponDefinitionIds: ReadonlyArray<string>;
}

/**
 * Replace the product's applicable coupons
 * (PUT /api/admin/v1/coupons/by-product/{productId}).
 * 빈 리스트 = 이 상품의 모든 쿠폰 매핑 해제. 이벤트 sync 와 동일하게 상품 저장 onSuccess
 * 이후 별도 호출한다.
 */
export const useSyncProductCouponsMutation = () => {
  const qc = useQueryClient();
  return useMutation<CouponDefinition[], Error, SyncProductCouponsInput>({
    mutationFn: async ({ productId, couponDefinitionIds }) => {
      const path = `/api/admin/v1/coupons/by-product/${productId}`;
      const body = { couponDefinitionIds: [...couponDefinitionIds] };
      const res = await adminFetch(path, {
        method: 'PUT',
        headers: { 'Idempotency-Key': idempotencyKeyFor('PUT', path, body) },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`product-coupons sync failed: ${res.status}`);
      const env = (await res.json()) as { data?: CouponDefinition[] };
      return Array.isArray(env.data) ? env.data : [];
    },
    onSuccess: (_data, { productId }) => {
      void qc.invalidateQueries({ queryKey: productCouponsQueryKey(productId) });
    },
  });
};
